import { AdaptivePagination } from "@/components/flexkit/AdaptivePagination";
import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { CompactProjectCard } from "@/components/projects/cards/CompactProjectCard";
import { ProjectCard } from "@/components/projects/cards/ProjectCard";
import { getRepoUrlFromInitializer } from "@/queries/environment-queries";
import { useListGroups, useListRoleAssignments } from "@/queries/group-queries";
import { useMembers } from "@/queries/organization-queries";
import { useListProjectStates, usePaginatedProjects } from "@/queries/project-queries";
import { useListAllRunnerSCMIntegrations } from "@/queries/runner-configuration-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import { parseAsInteger, useQueryState } from "nuqs";
import { useEffect, useMemo, type FC } from "react";

export const ProjectsListPaginated: FC<{
    canEdit?: boolean;
    showRecentlyUsedOnly?: boolean;
    search?: string;
    selectEnvironmentClass?: string;
    selectRunnerId?: string;
    useCompactLayout?: boolean;
    onCreateSuccess?: () => void;
}> = ({
    canEdit,
    search,
    selectEnvironmentClass,
    selectRunnerId,
    useCompactLayout,
    onCreateSuccess,
}) => {
    const { data: user, isLoading: isLoadingUser } = useAuthenticatedUser();

    // Page-based pagination state (1-based in URL, converted to 0-based for API)
    const [currentPage, setCurrentPage] = useQueryState("page", parseAsInteger.withDefault(1));

    const {
        data: projects,
        isPending: isPendingProjects,
        isFetching: isFetchingProjects,
        totalCount,
        totalPages,
        pageSize,
    } = usePaginatedProjects({
        search,
        runnerIds: selectRunnerId ? [selectRunnerId] : undefined,
        // 0-based page numbers for our API calls
        page: currentPage - 1,
        enabled: true,
    });

    const projectIds = useMemo(() => projects.map((p) => p.id), [projects]);
    const { data: projectsStates } = useListProjectStates(projectIds);
    const { data: groupsData } = useListGroups();

    // Fetch all role assignments for PROJECT resources once at the list level
    // This is more efficient than fetching per-project in each card
    // Omit resourceId to fetch all assignments for this resource type
    const { data: allProjectRoleAssignments } = useListRoleAssignments({
        resourceType: ResourceType.PROJECT,
        enabled: true,
    });

    const { data: membersData } = useMembers();

    const allProjects = useMemo(() => {
        return projects.map((project) => {
            return {
                project,
                state: projectsStates?.find((p) => p.projectId === project.id),
            };
        });
    }, [projects, projectsStates]);

    const { data: scmIntegrations } = useListAllRunnerSCMIntegrations();

    const filteredByState = useMemo(() => {
        return allProjects.filter((p) => {
            if (selectEnvironmentClass) {
                if (selectEnvironmentClass === "local") {
                    return !!p.state?.environmentClasses?.find(
                        (ec) => ec.environmentClass.case === "localRunner" && ec.environmentClass.value === true,
                    );
                } else {
                    return !!p.state?.environmentClasses?.find(
                        (ec) =>
                            ec.environmentClass.case === "environmentClassId" &&
                            ec.environmentClass.value === selectEnvironmentClass,
                    );
                }
            }
            // selectRunnerId filtering is now handled by the API via runnerIds filter

            return true;
        });
    }, [allProjects, selectEnvironmentClass]);

    // reset currentPage when:
    // - page is invalid (< 1)
    // - search changes
    // - there are no projects on the current page (there can be many reasons, such as different permissions/org/time frame)
    useEffect(() => {
        if (currentPage < 1) {
            void setCurrentPage(1);
        }
    }, [currentPage, setCurrentPage]);
    useEffect(() => {
        if (allProjects.length === 0 && !isPendingProjects) {
            void setCurrentPage(1);
        }
    }, [allProjects.length, isPendingProjects, setCurrentPage]);
    useEffect(() => {
        if (search) {
            void setCurrentPage(1);
        }
    }, [search, setCurrentPage]);

    const isLoading = isLoadingUser || !user || isPendingProjects;
    if (isLoading) {
        return <LoadingState delay={true} />;
    }

    const noProjects = allProjects.length === 0;

    return (
        <div className="flex w-full flex-col gap-4 pb-6" data-testid="projects-list-paginated">
            <ul
                className={
                    useCompactLayout
                        ? "flex w-full flex-col gap-2"
                        : "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3"
                }
            >
                {filteredByState.map(({ project, state }) => {
                    const projectContextUrl = getRepoUrlFromInitializer(project.initializer);
                    const scmId =
                        projectContextUrl &&
                        scmIntegrations?.find((int) => int.host === new URL(projectContextUrl).host)?.scmId;

                    // Filter role assignments to only those for this specific project
                    const projectRoleAssignments = allProjectRoleAssignments?.assignments?.filter(
                        (a) => a.resourceId === project.id,
                    );

                    return (
                        <li key={project.id}>
                            {useCompactLayout ? (
                                <CompactProjectCard
                                    project={project}
                                    orgMembers={membersData?.members}
                                    onCreateSuccess={onCreateSuccess}
                                />
                            ) : (
                                <SkeletonBlock className="h-full min-h-[180px]" ready={!isFetchingProjects}>
                                    <ProjectCard
                                        canEdit={useCompactLayout ? false : canEdit ?? false}
                                        project={project}
                                        state={state}
                                        orgMembers={membersData?.members}
                                        currentUserId={user?.id ?? ""}
                                        onCreateSuccess={onCreateSuccess}
                                        scmId={scmId}
                                        roleAssignments={projectRoleAssignments}
                                        allGroups={groupsData?.groups}
                                    />
                                </SkeletonBlock>
                            )}
                        </li>
                    );
                })}
            </ul>

            {noProjects &&
                (search?.trim() ? (
                    <EmptyStateCard
                        title="No results found"
                        description="No projects match your search criteria."
                        data-testid="no-search-results-empty-state"
                    />
                ) : (
                    <EmptyStateCard
                        title="No projects"
                        description="Projects bundle your repo, secrets, and other configuration into a shareable template—prebuilt in the background for faster startup times. Only admins can create projects for your organization."
                        data-testid="no-projects-empty-state"
                    />
                ))}

            <AdaptivePagination
                totalCount={totalCount}
                totalPages={totalPages}
                currentPage={currentPage - 1}
                onPageChange={(page) => {
                    void setCurrentPage(page + 1);
                }}
                isLoading={isFetchingProjects}
                currentItemsCount={projects.length}
                pageSize={pageSize}
            />
        </div>
    );
};
